
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef, useCallback } from "react";
import { translate } from "../services/AppService";
import * as echarts from 'echarts';//https://echarts.apache.org/examples/en/editor.html?c=line-stack
import * as app from "../services/AppService";
import { Button } from "../components";
export interface ChartData {
    name: string;
    value: number;
    data?: number[];
}
interface AreaChartProps {
    title?: string;
    subTitle?: string;
    id?: string;
    height_px?: string;
    data: ChartData[];
    xAxisData?: string[];
    visualMapPieces?: { gt: number; lt: number; color: string }[];
}

function getCssVar(v: string) {
    return getComputedStyle(document.documentElement).getPropertyValue(v).trim();
}

function AreaChart(props: AreaChartProps) {
    const [chart, setChart] = useState<echarts.ECharts | null>(null);
    const [showSymbol, setShowSymbol] = useState(true);
    const containerRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<HTMLDivElement>(null);

    const hasData = props.data.length > 0;
    // ── 1. Init echarts once ─────────────────────────────────────────
    useEffect(() => {
        if (!chartRef.current) return;
        const existing = echarts.getInstanceByDom(chartRef.current);
        if (existing) existing.dispose();

        const instance = echarts.init(chartRef.current, null, {
            renderer: 'canvas',
            useDirtyRect: false,
        });
        setChart(instance);
        return () => {
            instance.dispose();
            setChart(null);
        };
    }, []);

    // ── 2. Apply options ─────────────────────────────────────────────
    const applyOptions = useCallback(() => {
        if (!chart || props.data.length === 0) return;

        if (chartRef.current) {
            const { width, height } = chartRef.current.getBoundingClientRect();
            if (width === 0 || height === 0) {
                setTimeout(() => applyOptions(), 100);
                return;
            }
        }
        const dark = app.getCookie("colorMode", "dark") === "dark";
        const textColor = dark ? 'rgba(220,220,220,0.9)' : 'rgba(50,50,50,0.9)';
        const gridColor = dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
        const tooltipBg = dark ? '#1e2d3d' : '#ffffff';
        const tooltipBorder = dark ? '#2d4a5a' : '#dee2e6';
        const tooltipcolor = dark ? "rgba(220,220,220,0.9)" : "rgba(50,50,50,0.9)"

        const primary = getCssVar('--bs-primary');
        const success = getCssVar('--bs-success');
        const info = getCssVar('--bs-info');
        const warning = getCssVar('--bs-warning');
        const danger = getCssVar('--bs-danger');
        const secondary = getCssVar('--bs-secondary');

        const allNumbers = props.xAxisData
            ? props.data.map(r => r.data!).flat(1)
            : props.data.map(r => r.value);
        const max = Math.max(...allNumbers);
        let min = Math.min(...allNumbers);
        min = min - Math.abs(min - max) / 50;

        const buildSeries = (): echarts.SeriesOption[] => {
            if (props.xAxisData) {
                return props.data.map((s) => ({
                    name: s.name,
                    data: s.data,
                    type: "line" as const,
                    smooth: 0.6,
                    showSymbol,
                    symbol: "circle",
                    symbolSize: 6,
                    lineStyle: { width: 2 },
                }));
            }
            return [{
                data: props.data as any,
                type: "line" as const,
                smooth: 0.6,
                showSymbol,
                symbol: "circle",
                symbolSize: 6,
                lineStyle: { width: 2 },
            }];
        };
        const option: echarts.EChartsOption = {
            backgroundColor: 'transparent',
            color: [primary, success, info, warning, danger, secondary],

            ...(props.visualMapPieces && {
                visualMap: {
                    type: "piecewise",
                    show: false,
                    dimension: 0,
                    seriesIndex: 0,
                    pieces: props.visualMapPieces,
                },
            }),

            legend: {
                type: 'scroll',
                orient: 'horizontal',
                bottom: 0,
                textStyle: { color: textColor },
                pageTextStyle: { color: textColor },
                data: props.data.map((r) => r.name),
            },
            textStyle: { color: textColor, fontSize: 12 },
            tooltip: {
                trigger: 'axis',
                confine: true,
                backgroundColor: tooltipBg,
                borderColor: tooltipBorder,
                textStyle: { color: tooltipcolor },
            },
            title: { show: false },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '15%',
                top: '5%',
                containLabel: true,
            },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: props.xAxisData ?? props.data.map((r) => r.name),
                axisLabel: { color: textColor, rotate: 30, overflow: 'truncate', width: 80 },
                axisLine: { lineStyle: { color: gridColor } },
                axisTick: { lineStyle: { color: gridColor } },
            },
            yAxis: {
                type: 'value',
                boundaryGap: [0, "30%"],
                min,
                max,
                axisLabel: { color: textColor },
                axisLine: { lineStyle: { color: gridColor } },
                splitLine: { lineStyle: { color: gridColor, type: 'dashed' } },
            },
            series: buildSeries(),
        };

        chart.setOption(option, true);
        setTimeout(() => chart.resize(), 50);
    }, [chart, props.data, props.xAxisData, showSymbol]);

    useEffect(() => { applyOptions(); }, [applyOptions]);

    // ── 3. Theme observer ────────────────────────────────────────────
    useEffect(() => {
        const observer = new MutationObserver(() => applyOptions());
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['data-bs-theme', 'class'],
        });
        return () => observer.disconnect();
    }, [applyOptions]);

    // ── 4. Resize listeners ──────────────────────────────────────────
    const handleResize = useCallback(() => { chart?.resize(); }, [chart]);

    useEffect(() => {
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [handleResize]);

    useEffect(() => {
        if (!containerRef.current) return;
        const ro = new ResizeObserver(() => {
            chart?.resize();
            applyOptions();
        });
        ro.observe(containerRef.current);
        return () => ro.disconnect();
    }, [chart, applyOptions]);

    // ── 5. Toolbar actions ───────────────────────────────────────────
    const handleSaveImage = useCallback(() => {
        if (!chart) return;
        const url = chart.getDataURL({ type: "png", pixelRatio: 2, backgroundColor: "#fff" });
        const a = document.createElement("a");
        a.href = url;
        a.download = `${props.title ?? "chart"}.png`;
        a.click();
    }, [chart, props.title]);

    // ── 6. Render ────────────────────────────────────────────────────
    return (
        <div
            ref={containerRef}
            className="overflow-hidden rounded"
            style={{
                border: hasData ? "1px solid var(--bs-primary-border-subtle)" : "none",
                background: "transparent",
            }}
        >
            {hasData && (
                <div
                    className="px-3 bg-primary-subtle py-2 d-flex align-items-center gap-2"
                    style={{
                        borderBottom: '1px solid var(--bs-primary)',
                    }}
                >
                    {/* Title — centered */}
                    <div className="d-flex flex-grow-1 align-items-center justify-content-center gap-2">
                        <span className="fw-bold fs-5" style={{ color: "var(--bs-light)" }}>
                            {translate(props.title ?? "")}
                        </span>
                        {props.subTitle && (
                            <span className="opacity-75 small" style={{ color: "var(--bs-dark)" }}>
                                {translate(props.subTitle)}
                            </span>
                        )}
                    </div>
                    {/* Toolbar buttons */}
                    <div className="d-flex align-items-center gap-2">
                        <Button
                            label="Dots"
                            faIcon={`fa-solid fa-circle-dot ${showSymbol ? "text-success" : "text-secondary"}`}
                            labelClass={showSymbol ? "text-success" : "text-secondary"}
                            class={showSymbol
                                ? "bg-success-subtle border border-1 border-success"
                                : "bg-secondary-subtle border border-1 border-secondary"}
                            clicked={() => setShowSymbol(prev => !prev)}
                        />
                        <Button
                            clicked={handleSaveImage}
                            label="Download"
                            faIcon="fa-solid fa-file-image text-warning"
                            labelClass="text-warning"
                            class="bg-warning-subtle border border-1 border-warning"
                        />
                    </div>
                </div>
            )}

            <div
                ref={chartRef}
                style={{
                    width: '100%',
                    height: props.height_px
                        ? `${props.height_px}px`
                        : 'clamp(220px, 40vw, 500px)',
                    display: hasData ? "block" : "none",
                }}
            />
        </div>
    );
}

export default AreaChart;